/**
 * Shared number formatting utilities
 */

/**
 * Rounds a number to the specified decimal places
 * @param value - The value to round
 * @param decimals - Number of decimal places (default 0)
 * @returns Rounded number
 */
export const roundToDecimal = (value: number, decimals: number = 0): number => {
  const multiplier = Math.pow(10, decimals);
  return Math.round(value * multiplier) / multiplier;
};

/**
 * Formats a GPR (Global Player Rating) value
 * @param gpr - The GPR value to format
 * @returns Rounded GPR value
 */
export const formatGPR = (gpr?: number | null): number => {
  if (gpr === undefined || gpr === null || !Number.isFinite(gpr)) return 0;
  return Math.round(gpr);
};

/**
 * Formats a score value to an integer
 * @param score - The score value to format
 * @returns Rounded score or 0 if invalid
 */
export const formatScore = (score?: number | null): number => {
  if (score === undefined || score === null || !Number.isFinite(score))
    return 0;
  return Math.round(score);
};

/**
 * Calculates and formats a difference between two values
 * @param value1 - First value
 * @param value2 - Second value
 * @param decimals - Number of decimal places for rounding
 * @returns The rounded-sm difference
 */
export const calculateDifference = (
  value1: number,
  value2: number,
  decimals: number = 1
): number => {
  return roundToDecimal(value1, decimals) - roundToDecimal(value2, decimals);
};
